
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCategories } from '@/hooks/useCategories';

interface CategorySelectorProps {
  subcategoryId: number | null;
  onSubcategoryChange: (subcategoryId: number | null) => void;
  disabled?: boolean;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  subcategoryId,
  onSubcategoryChange,
  disabled = false
}) => {
  const { categories, subcategories, subcategoriesByParent, loading } = useCategories();
  
  // Independent category state that persists regardless of subcategory
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  // Initialize category state based on the current subcategory
  useEffect(() => {
    if (subcategoryId && subcategories.length > 0) {
      const subcategory = subcategories.find(sub => sub.id === subcategoryId);
      if (subcategory?.parent_category) {
        setSelectedCategoryId(subcategory.parent_category.toString());
      }
    }
  }, [subcategoryId, subcategories]);

  // Get available subcategories for the selected category
  const availableSubcategories = React.useMemo(() => {
    if (!selectedCategoryId) return [];
    const categoryId = parseInt(selectedCategoryId);
    return subcategoriesByParent[categoryId] || [];
  }, [selectedCategoryId, subcategoriesByParent]);

  const handleCategoryChange = (categoryId: string) => {
    console.log('Category selected:', categoryId);
    // Store the selected category
    setSelectedCategoryId(categoryId);
    // Reset subcategory when category changes
    onSubcategoryChange(null);
  };

  const handleSubcategoryChange = (subcategoryIdStr: string) => {
    console.log('Subcategory selected:', subcategoryIdStr);
    const newSubcategoryId = subcategoryIdStr === '' ? null : parseInt(subcategoryIdStr, 10);
    onSubcategoryChange(newSubcategoryId);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <Label>Category</Label>
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
        </div>
        <div>
          <Label>Subcategory</Label>
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-4 text-destructive">
          Failed to load categories. Please refresh the page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="category">Category</Label>
        <Select
          value={selectedCategoryId}
          onValueChange={handleCategoryChange}
          disabled={disabled}
        >
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent className="bg-white z-50">
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id.toString()}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="subcategory">Subcategory</Label>
        <Select
          value={subcategoryId ? subcategoryId.toString() : ''}
          onValueChange={handleSubcategoryChange}
          disabled={disabled || availableSubcategories.length === 0}
        >
          <SelectTrigger className="bg-white">
            <SelectValue placeholder={
              !selectedCategoryId 
                ? "Select a category first" 
                : availableSubcategories.length === 0
                  ? "No subcategories available"
                  : "Select subcategory"
            } />
          </SelectTrigger>
          <SelectContent className="bg-white z-50">
            {availableSubcategories.map((subcategory) => (
              <SelectItem key={subcategory.id} value={subcategory.id.toString()}>
                {subcategory.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
